// Hybrid Upload Service - Immediate UI + Auto-Verification Fallback
import { showToast } from "../utils/show-toast";
import { normalizeApiError, handleApiCall } from "../utils/apiResponseHandler";

import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";

// Hybrid Upload types
export interface HybridVerificationRequest {
  uploadId: string;
  filePath: string;
}

export interface HybridVerificationResponse {
  verified: boolean;
  uploadId: string;
  downloadUrl?: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  checksum?: string;
  message: string;
}

export interface UploadStatusResponse {
  status: 'PENDING' | 'PROCESSING' | 'ACTIVE' | 'FAILED';
  uploadId: string;
  downloadUrl?: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  message: string;
  processedAt?: string;
}

export interface SignedUploadRequest {
  fileName: string;
  fileType: string;
  fileSize: number;
  folder?: string;
}

export interface SignedUploadResponse {
  uploadId: string;
  signedUrl: string;
  filePath: string;
  expiresAt: string;
}

export interface HybridUploadResult {
  success: boolean;
  uploadId: string;
  downloadUrl?: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  verified: boolean;
  isProcessing?: boolean;
}

export interface UploadProgressCallback {
  (progress: number): void;
}

export interface ProcessingStatusCallback {
  (isProcessing: boolean, message?: string): void;
}

class HybridUploadService {
  private pollingTimeouts = new Map<string, NodeJS.Timeout>();
  private maxPollingAttempts = 30; // 5 minutes at 10 second intervals
  private pollingInterval = 10000; // 10 seconds

  // Step 1: Request signed upload URL from backend
  async getSignedUploadUrl(file: File, folder = 'uploads'): Promise<SignedUploadResponse> {
    try {
      const request: SignedUploadRequest = {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        folder: folder
      };

      const response = await handleApiCall(async () => await api.post<{
        statusCode: number;
        message: string;
        data: SignedUploadResponse;
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.upload.getSignedUrl),
        body: request,
        auth: true,
      }));

      // handleApiCall already extracts the data, so response IS the data
      // Handle cases where data might be nested or direct
      const signedUrlData = response?.data || response;

      if (!signedUrlData || !signedUrlData.uploadId || !signedUrlData.signedUrl) {

        throw new Error('Invalid response format from signed URL API');
      }

      return signedUrlData;
    } catch (error) {

      const n = normalizeApiError(error);
      throw new Error(n.message || 'Failed to get upload URL');
    }
  }

  // Step 2: Upload file directly to Supabase using signed URL with progress.
  // Storage can save a file and never reply (2026-11-02 outage). Once every byte is sent with no
  // reply, `landed` is asked at 10 s, then every 5 s; if the file is there, stop waiting.
  async uploadToSupabase(
    signedUrl: string,
    file: File,
    onProgress?: UploadProgressCallback,
    landed?: () => Promise<boolean>
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let timer: ReturnType<typeof setTimeout> | undefined;
      let settled = false;
      const settle = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) reject(error);
        else resolve();
      };
      const checkLanded = async () => {
        if (settled || !landed) return;
        if (await landed()) {
          settle();
          xhr.abort();
        } else if (!settled) {
          timer = setTimeout(checkLanded, 5000);
        }
      };

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = 30 + (e.loaded / e.total) * 60; // 30-90%
          onProgress(progress);
        }
      });
      xhr.upload.addEventListener('load', () => {
        if (landed) timer = setTimeout(checkLanded, 10000);
      });

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          settle();
        } else {
          settle(Object.assign(new Error(`Upload failed: ${xhr.statusText}`), { status: xhr.status }));
        }
      };

      xhr.onerror = () => settle(new Error('Upload failed'));

      // Send the raw file directly, NOT wrapped in FormData
      // FormData adds multipart boundaries which corrupts binary files
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
  }

  // Step 3: Try immediate hybrid verification
  async verifyHybrid(uploadId: string, filePath: string): Promise<HybridVerificationResponse> {
    try {
      const request: HybridVerificationRequest = {
        uploadId,
        filePath
      };

      const response = await handleApiCall(async () => await api.post<{
        statusCode: number;
        message: string;
        data: HybridVerificationResponse;
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.upload.verifyHybrid),
        body: request,
        auth: true,
      }));

      // handleApiCall already extracts the data
      const verificationData = response?.data || response;

      if (!verificationData || !verificationData.uploadId) {
        throw new Error('Invalid response format from hybrid verification API');
      }

      return verificationData;
    } catch (error) {

      const n = normalizeApiError(error);
      throw new Error(n.message || 'Failed to verify upload');
    }
  }

  // Poll upload status for background processing
  async getUploadStatus(uploadId: string): Promise<UploadStatusResponse> {
    try {
      const response = await handleApiCall(async () => await api.get<{
        statusCode: number;
        message: string;
        data: UploadStatusResponse;
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.upload.getUploadStatus(uploadId)),
        auth: true,
      }));

      // handleApiCall already extracts the data
      const statusData = response?.data || response;

      if (!statusData || !statusData.uploadId) {
        throw new Error('Invalid response format from status API');
      }

      return statusData;
    } catch (error) {

      const n = normalizeApiError(error);
      throw new Error(n.message || 'Failed to get upload status');
    }
  }

  // Start polling for upload completion
  async startPolling(
    uploadId: string, 
    onStatusUpdate: ProcessingStatusCallback,
    onComplete: (result: UploadStatusResponse) => void
  ): Promise<void> {
    // Clear any existing polling for this upload
    this.stopPolling(uploadId);

    let attemptCount = 0;
    
    const poll = async () => {
      try {
        attemptCount++;
        
        if (attemptCount > this.maxPollingAttempts) {
          onStatusUpdate(false, 'Processing timeout. Please refresh the page to see if your file was uploaded.');
          showToast('Upload processing is taking longer than expected. Please refresh the page.', 'warning');
          return;
        }

        const status = await this.getUploadStatus(uploadId);

        if (status.status === 'ACTIVE') {
          onStatusUpdate(false, 'Upload completed successfully!');
          onComplete(status);
          this.stopPolling(uploadId);
          return;
        }
        
        if (status.status === 'FAILED') {
          onStatusUpdate(false, 'Upload processing failed. Please try uploading again.');
          showToast('Upload processing failed. Please try again.', 'error');
          this.stopPolling(uploadId);
          return;
        }

        // Continue polling for PENDING or PROCESSING
        onStatusUpdate(true, `Processing upload... (${attemptCount}/${this.maxPollingAttempts})`);
        
        const timeoutId = setTimeout(poll, this.pollingInterval);
        this.pollingTimeouts.set(uploadId, timeoutId);
        
      } catch (error) {

        onStatusUpdate(false, 'Error checking upload status. Please refresh the page.');
        this.stopPolling(uploadId);
      }
    };

    // Start polling immediately
    setTimeout(poll, this.pollingInterval);
  }

  // Stop polling for a specific upload
  stopPolling(uploadId: string): void {
    const timeoutId = this.pollingTimeouts.get(uploadId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.pollingTimeouts.delete(uploadId);
    }
  }

  // Stop all polling
  stopAllPolling(): void {
    this.pollingTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.pollingTimeouts.clear();
  }

  // Complete hybrid upload with immediate UI + fallback polling
  async hybridUpload(
    file: File, 
    folder = 'uploads',
    onProgress?: UploadProgressCallback,
    onStatusUpdate?: ProcessingStatusCallback
  ): Promise<HybridUploadResult> {
    try {
      // Step 1: Get signed URL
      onProgress?.(10);
      const { uploadId, signedUrl, filePath } = await this.getSignedUploadUrl(file, folder);
      
      // Step 2: Upload to Supabase with progress. A failed or silent upload first asks our backend
      // whether the file landed anyway; if not, a 5xx or network error is retried up to twice.
      onProgress?.(30);
      let confirmed: HybridVerificationResponse | undefined;
      const landed = async () => {
        const r = await this.verifyHybrid(uploadId, filePath).catch(() => undefined);
        if (r?.verified) confirmed = r;
        return !!r?.verified;
      };
      for (let attempt = 1; ; attempt++) {
        try {
          await this.uploadToSupabase(signedUrl, file, onProgress, landed);
          break;
        } catch (error) {
          if (confirmed || (await landed())) break;
          const status = (error as { status?: number }).status ?? 0;
          if (attempt === 3 || (status >= 400 && status < 500)) throw error;
          await new Promise((r) => setTimeout(r, attempt * 1500));
        }
      }

      // Step 3: Try immediate hybrid verification (already done if Step 2 had to confirm it)
      onProgress?.(95);
      const hybridResult = confirmed ?? (await this.verifyHybrid(uploadId, filePath));
      onProgress?.(100);
      
      if (hybridResult.verified) {
        // Immediate success - show file in UI right away
        onStatusUpdate?.(false, 'Upload completed successfully!');
        return {
          success: true,
          uploadId: hybridResult.uploadId,
          downloadUrl: hybridResult.downloadUrl,
          filePath: hybridResult.filePath,
          fileSize: hybridResult.fileSize,
          mimeType: hybridResult.mimeType,
          verified: true,
          isProcessing: false
        };
      } else {
        // Needs background processing - show processing message and start polling
        onStatusUpdate?.(true, 'Processing upload in the background...');
        
        return {
          success: true,
          uploadId: hybridResult.uploadId,
          downloadUrl: undefined, // No URL yet
          filePath: hybridResult.filePath,
          fileSize: undefined,
          mimeType: undefined,
          verified: false,
          isProcessing: true
        };
      }
    } catch (error) {

      onStatusUpdate?.(false, 'Upload failed. Please try again.');
      throw error;
    }
  }

  // Get secure download URL (reuse from original service)
  async getSecureDownloadUrl(uploadId: string): Promise<string> {
    try {
      const response = await handleApiCall(async () => await api.get<{
        statusCode: number;
        message: string;
        data: { downloadUrl: string };
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.upload.getDownloadUrl(uploadId)),
        auth: true,
      }));

      // handleApiCall already extracts the data
      const downloadData = response?.data || response;

      if (!downloadData?.downloadUrl) {
        throw new Error('Invalid response format from download URL API');
      }

      return downloadData.downloadUrl;
    } catch (error) {

      const n = normalizeApiError(error);
      throw new Error(n.message || 'Failed to get download URL');
    }
  }

  // Download file using secure URL
  async downloadFile(uploadId: string, filename: string): Promise<void> {
    try {
      const secureUrl = await this.getSecureDownloadUrl(uploadId);
      
      const link = document.createElement('a');
      link.href = secureUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {

      showToast('Failed to download file', 'error');
    }
  }

  // Handle upload errors with user-friendly messages
  handleUploadError(error: any): void {
    let message = 'Upload failed. Please try again.';
    
    if (error.message.includes('File type not allowed')) {
      message = 'File type not supported. Please use PDF, DOCX, XLSX, or images.';
    } else if (error.message.includes('File size exceeds')) {
      message = 'File is too large. Maximum size is 100MB.';
    } else if (error.message.includes('Upload record is not in pending state')) {
      message = 'Upload session expired. Please try again.';
    } else if (error.message.includes('timeout')) {
      message = 'Upload timed out. Please check your connection and try again.';
    } else if (error.message.includes('Network')) {
      message = 'Network error. Please check your connection and try again.';
    }
    
    showToast(message, 'error');
  }

  // Cleanup method for component unmounting
  cleanup(): void {
    this.stopAllPolling();
  }
}

// Export singleton instance
export const hybridUploadService = new HybridUploadService();
export default hybridUploadService;